import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import CoverGallery from "../components/CoverGallery";
import { generateBookCovers } from "../services/imageGenerationService";
import { getBookById, updateBook } from "../services/booksService";
import { uploadToCloudinary } from "../services/cloudinaryService";
import Stepper from "../components/Stepper";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import DocxEditorModal from "../components/DocxEditorModal";

const CoverSelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [book, setBook] = useState(null);
    const [frontCovers, setFrontCovers] = useState([]);
    const [backCovers, setBackCovers] = useState([]);
    const [selectedFront, setSelectedFront] = useState(null);
    const [selectedBack, setSelectedBack] = useState(null);
    const [user, setUser] = useState(null);

    // Custom styling options states
    const [coverMode, setCoverMode] = useState("image");
    const [coverBgColor, setCoverBgColor] = useState("#DC2626");
    const [coverFont, setCoverFont] = useState("serif");

    // Front Cover specific styling
    const [titleColor, setTitleColor] = useState("#FFFFFF");
    const [titleSize, setTitleSize] = useState(28);
    const [titleTop, setTitleTop] = useState(35);
    const [titleLeft, setTitleLeft] = useState(10);
    const [titleWidth, setTitleWidth] = useState(60);
    const [titleFontWeight, setTitleFontWeight] = useState("bold");
    const [titleFontStyle, setTitleFontStyle] = useState("normal");
    const [titleAlign, setTitleAlign] = useState("center");
    const [titleCase, setTitleCase] = useState("uppercase");

    const [authorColor, setAuthorColor] = useState("#FFFFFF");
    const [authorSize, setAuthorSize] = useState(16);
    const [authorTop, setAuthorTop] = useState(75);
    const [authorLeft, setAuthorLeft] = useState(10);
    const [authorWidth, setAuthorWidth] = useState(60);
    const [authorFontWeight, setAuthorFontWeight] = useState("bold");
    const [authorFontStyle, setAuthorFontStyle] = useState("normal");
    const [authorAlign, setAuthorAlign] = useState("center");
    const [authorCase, setAuthorCase] = useState("uppercase");

    // Back Cover specific styling
    const [descColor, setDescColor] = useState("#FFFFFF");
    const [descSize, setDescSize] = useState(13);
    const [descTop, setDescTop] = useState(15);
    const [descLeft, setDescLeft] = useState(8);
    const [descWidth, setDescWidth] = useState(60);
    const [descFontWeight, setDescFontWeight] = useState("normal");
    const [descFontStyle, setDescFontStyle] = useState("italic");
    const [descAlign, setDescAlign] = useState("left");
    const [descCase, setDescCase] = useState("normal");

    const [profileColor, setProfileColor] = useState("#FFFFFF");
    const [profileSize, setProfileSize] = useState(10);
    const [profileTop, setProfileTop] = useState(65);
    const [profileLeft, setProfileLeft] = useState(8);
    const [profileWidth, setProfileWidth] = useState(60);
    const [profileFontWeight, setProfileFontWeight] = useState("normal");
    const [profileFontStyle, setProfileFontStyle] = useState("italic");
    const [profileAlign, setProfileAlign] = useState("left");
    const [profileCase, setProfileCase] = useState("normal");

    // Cover custom text
    const [coverTitle, setCoverTitle] = useState("");
    const [coverAuthor, setCoverAuthor] = useState("");
    const [coverDescription, setCoverDescription] = useState("");
    const [coverProfileText, setCoverProfileText] = useState("");

    // Editor control states
    const [activeDesignTab, setActiveDesignTab] = useState("front");
    const [selectedElement, setSelectedElement] = useState(null);
    const [activeDrag, setActiveDrag] = useState(null);
    const [activeResize, setActiveResize] = useState(null);
    const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Refs for elements
    const elementRefs = useRef({});
    const savedSelectionRef = useRef(null);

    const getBookAspect = (type) => {
        const types = {
            pocket_book: { w: 4.25, h: 6.87 },
            small_paperback: { w: 5, h: 8 },
            digest_size: { w: 5.5, h: 8.5 },
            trade_paperback: { w: 6, h: 9 },
            large_book: { w: 7, h: 10 },
            childrens_book: { w: 8, h: 10 },
            square_book: { w: 8.5, h: 8.5 },
            textbook: { w: 8.5, h: 11 },
            a5: { w: 5.83, h: 8.27 },
            a4: { w: 8.27, h: 11.69 },
        };
        const dim = types[type] || types.trade_paperback;
        return `${dim.w} / ${dim.h}`;
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((u) => {
            if (u) {
                setUser(u);
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const bookId = new URLSearchParams(location.search).get("bookId");

    useEffect(() => {
        const loadBookAndCovers = async () => {
            try {
                setLoading(true);
                const bookData = await getBookById(bookId);
                if (!bookData) {
                    alert("Book not found!");
                    navigate("/");
                    return;
                }
                setBook(bookData);

                // Load existing custom styling
                if (bookData.coverMode) setCoverMode(bookData.coverMode);
                if (bookData.coverBgColor) setCoverBgColor(bookData.coverBgColor);
                if (bookData.coverFont) setCoverFont(bookData.coverFont);

                if (bookData.titleColor) setTitleColor(bookData.titleColor);
                if (bookData.titleSize) setTitleSize(bookData.titleSize);
                if (bookData.titleTop !== undefined) setTitleTop(bookData.titleTop);
                if (bookData.titleLeft !== undefined) setTitleLeft(bookData.titleLeft);
                if (bookData.titleWidth !== undefined) setTitleWidth(bookData.titleWidth);
                if (bookData.titleFontWeight) setTitleFontWeight(bookData.titleFontWeight);
                if (bookData.titleFontStyle) setTitleFontStyle(bookData.titleFontStyle);

                if (bookData.authorColor) setAuthorColor(bookData.authorColor);
                if (bookData.authorSize) setAuthorSize(bookData.authorSize);
                if (bookData.authorTop !== undefined) setAuthorTop(bookData.authorTop);
                if (bookData.authorLeft !== undefined) setAuthorLeft(bookData.authorLeft);
                if (bookData.authorWidth !== undefined) setAuthorWidth(bookData.authorWidth);
                if (bookData.authorFontWeight) setAuthorFontWeight(bookData.authorFontWeight);
                if (bookData.authorFontStyle) setAuthorFontStyle(bookData.authorFontStyle);

                if (bookData.descColor) setDescColor(bookData.descColor);
                if (bookData.descSize) setDescSize(bookData.descSize);
                if (bookData.descTop !== undefined) setDescTop(bookData.descTop);
                if (bookData.descLeft !== undefined) setDescLeft(bookData.descLeft);
                if (bookData.descWidth !== undefined) setDescWidth(bookData.descWidth);
                if (bookData.descFontWeight) setDescFontWeight(bookData.descFontWeight);
                if (bookData.descFontStyle) setDescFontStyle(bookData.descFontStyle);

                if (bookData.profileColor) setProfileColor(bookData.profileColor);
                if (bookData.profileSize) setProfileSize(bookData.profileSize);
                if (bookData.profileTop !== undefined) setProfileTop(bookData.profileTop);
                if (bookData.profileLeft !== undefined) setProfileLeft(bookData.profileLeft);
                if (bookData.profileWidth !== undefined) setProfileWidth(bookData.profileWidth);
                if (bookData.profileFontWeight) setProfileFontWeight(bookData.profileFontWeight);
                if (bookData.profileFontStyle) setProfileFontStyle(bookData.profileFontStyle);

                setCoverTitle(bookData.coverTitle !== undefined ? bookData.coverTitle : (bookData.title || ""));
                setCoverAuthor(bookData.coverAuthor !== undefined ? bookData.coverAuthor : (bookData.author || ""));
                setCoverDescription(bookData.coverDescription !== undefined ? bookData.coverDescription : (bookData.aboutAuthor || ""));
                setCoverProfileText(bookData.coverProfileText !== undefined ? bookData.coverProfileText : (bookData.description || ""));

                if (bookData.titleAlign) setTitleAlign(bookData.titleAlign);
                if (bookData.authorAlign) setAuthorAlign(bookData.authorAlign);
                if (bookData.descAlign) setDescAlign(bookData.descAlign);
                if (bookData.profileAlign) setProfileAlign(bookData.profileAlign);

                if (bookData.titleCase) setTitleCase(bookData.titleCase);
                if (bookData.authorCase) setAuthorCase(bookData.authorCase);
                if (bookData.descCase) setDescCase(bookData.descCase);
                if (bookData.profileCase) setProfileCase(bookData.profileCase);

                const coverData = await generateBookCovers(
                    bookData.title,
                    bookData.author,
                    bookData.genre,
                    bookData.selectedSuggestion
                );

                setFrontCovers(coverData.front);
                setBackCovers(coverData.back);

                if (!bookData.frontCover && coverData.front?.length > 0) setSelectedFront(coverData.front[0]);
                else if (bookData.frontCover) setSelectedFront(bookData.frontCover);

                if (!bookData.backCover && coverData.back?.length > 0) setSelectedBack(coverData.back[0]);
                else if (bookData.backCover) setSelectedBack(bookData.backCover);

            } catch (error) {
                console.error("Error loading covers:", error);
                alert("Error loading cover designs.");
            } finally {
                setLoading(false);
            }
        };

        if (bookId) {
            loadBookAndCovers();
        } else {
            setLoading(false);
        }
    }, [bookId, navigate]);

    // Get element properties
    const getElementProps = (elementId) => {
        const props = {
            title: {
                top: titleTop,
                left: titleLeft,
                width: titleWidth,
                size: titleSize,
                color: titleColor,
                fontWeight: titleFontWeight,
                fontStyle: titleFontStyle,
                align: titleAlign,
                case: titleCase,
                setTop: setTitleTop,
                setLeft: setTitleLeft,
                setWidth: setTitleWidth,
                setSize: setTitleSize,
                setColor: setTitleColor,
                setFontWeight: setTitleFontWeight,
                setFontStyle: setTitleFontStyle,
                setAlign: setTitleAlign,
                setCase: setTitleCase,
                text: coverTitle || book?.title || "Untitled",
                setText: setCoverTitle,
            },
            author: {
                top: authorTop,
                left: authorLeft,
                width: authorWidth,
                size: authorSize,
                color: authorColor,
                fontWeight: authorFontWeight,
                fontStyle: authorFontStyle,
                align: authorAlign,
                case: authorCase,
                setTop: setAuthorTop,
                setLeft: setAuthorLeft,
                setWidth: setAuthorWidth,
                setSize: setAuthorSize,
                setColor: setAuthorColor,
                setFontWeight: setAuthorFontWeight,
                setFontStyle: setAuthorFontStyle,
                setAlign: setAuthorAlign,
                setCase: setAuthorCase,
                text: coverAuthor || book?.author || "Author",
                setText: setCoverAuthor,
            },
            description: {
                top: descTop,
                left: descLeft,
                width: descWidth,
                size: descSize,
                color: descColor,
                fontWeight: descFontWeight,
                fontStyle: descFontStyle,
                align: descAlign,
                case: descCase,
                setTop: setDescTop,
                setLeft: setDescLeft,
                setWidth: setDescWidth,
                setSize: setDescSize,
                setColor: setDescColor,
                setFontWeight: setDescFontWeight,
                setFontStyle: setDescFontStyle,
                setAlign: setDescAlign,
                setCase: setDescCase,
                text: coverDescription || book?.aboutAuthor || "Description",
                setText: setCoverDescription,
            },
            profile: {
                top: profileTop,
                left: profileLeft,
                width: profileWidth,
                size: profileSize,
                color: profileColor,
                fontWeight: profileFontWeight,
                fontStyle: profileFontStyle,
                align: profileAlign,
                case: profileCase,
                setTop: setProfileTop,
                setLeft: setProfileLeft,
                setWidth: setProfileWidth,
                setSize: setProfileSize,
                setColor: setProfileColor,
                setFontWeight: setProfileFontWeight,
                setFontStyle: setProfileFontStyle,
                setAlign: setProfileAlign,
                setCase: setProfileCase,
                text: coverProfileText || book?.description || "Profile",
                setText: setCoverProfileText,
            }
        };
        return props[elementId];
    };

    // Drag handlers - only triggered by drag handle
    const startDrag = (e, elementId) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(true);
        setIsEditing(false);

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const element = elementRefs.current[elementId];
        if (!element) return;
        const parent = element.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const rect = element.getBoundingClientRect();

        const offsetX = clientX - rect.left;
        const offsetY = clientY - rect.top;

        setActiveDrag({
            id: elementId,
            offsetX,
            offsetY,
            parentRect,
        });

        setSelectedElement(elementId);
        setShowFormattingToolbar(true);
    };

    const startResize = (e, elementId) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(true);
        setIsEditing(false);

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const element = elementRefs.current[elementId];
        if (!element) return;

        const parent = element.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const rect = element.getBoundingClientRect();

        setActiveResize({
            id: elementId,
            startX: clientX,
            startY: clientY,
            startWidth: rect.width,
            startHeight: rect.height,
            parentRect,
        });

        setSelectedElement(elementId);
        setShowFormattingToolbar(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;

            // Handle drag
            if (activeDrag) {
                const parent = activeDrag.parentRect;

                let leftPx = clientX - parent.left - activeDrag.offsetX;
                let topPx = clientY - parent.top - activeDrag.offsetY;

                let leftPct = Math.max(0, Math.min(85, (leftPx / parent.width) * 100));
                let topPct = Math.max(0, Math.min(90, (topPx / parent.height) * 100));

                leftPct = Math.round(leftPct * 10) / 10;
                topPct = Math.round(topPct * 10) / 10;

                const props = getElementProps(activeDrag.id);
                if (props) {
                    props.setLeft(leftPct);
                    props.setTop(topPct);
                }
            }

            // Handle resize
            if (activeResize) {
                const deltaX = (clientX - activeResize.startX) / activeResize.parentRect.width * 100;

                const newWidth = Math.max(15, Math.min(85, activeResize.startWidth / activeResize.parentRect.width * 100 + deltaX));

                const props = getElementProps(activeResize.id);
                if (props) {
                    props.setWidth(Math.round(newWidth * 10) / 10);
                }
            }
        };

        const handleMouseUp = () => {
            if (activeDrag || activeResize) {
                setIsDragging(false);
            }
            setActiveDrag(null);
            setActiveResize(null);
        };

        if (activeDrag || activeResize) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("touchmove", handleMouseMove);
            window.addEventListener("touchend", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("touchmove", handleMouseMove);
            window.removeEventListener("touchend", handleMouseUp);
        };
    }, [activeDrag, activeResize]);

    const getFontFamily = (f) => {
        if (f === "sans-serif") return "system-ui, -apple-system, sans-serif";
        if (f === "monospace") return "Courier New, monospace";
        return "Georgia, Times New Roman, serif";
    };

    const applyTextCase = (text, caseType) => {
        if (!text) return "";
        if (caseType === "uppercase") {
            return text.toUpperCase();
        } else if (caseType === "lowercase") {
            return text.toLowerCase();
        } else if (caseType === "capitalize") {
            return text.replace(/\b\w/g, char => char.toUpperCase());
        }
        return text;
    };

    const toggleFontWeight = (element) => {
        const props = getElementProps(element);
        if (!props) return;
        const newWeight = props.fontWeight === "bold" ? "normal" : "bold";
        props.setFontWeight(newWeight);
    };

    const toggleFontStyle = (element) => {
        const props = getElementProps(element);
        if (!props) return;
        const newStyle = props.fontStyle === "italic" ? "normal" : "italic";
        props.setFontStyle(newStyle);
    };

    const getCurrentElementData = () => {
        if (!selectedElement) return null;
        return getElementProps(selectedElement);
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedSelectionRef.current = sel.getRangeAt(0);
        }
    };

    const restoreSelection = () => {
        if (savedSelectionRef.current) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedSelectionRef.current);
        }
    };

    const applyStyleToSelection = (command, value = null) => {
        restoreSelection();
        document.execCommand(command, false, value);

        // Sync HTML back to state
        if (selectedElement) {
            if (selectedElement === "profile") {
                const authorEl = document.getElementById("profile-author-text");
                const bioEl = document.getElementById("profile-bio-text");
                if (authorEl) setCoverAuthor(authorEl.innerHTML);
                if (bioEl) setCoverProfileText(bioEl.innerHTML);
            } else {
                const activeEl = elementRefs.current[selectedElement];
                if (activeEl) {
                    const props = getElementProps(selectedElement);
                    if (props) {
                        props.setText(activeEl.innerHTML);
                    }
                }
            }
        }
    };

    const handleBoldClick = (e) => {
        e.preventDefault();
        const sel = window.getSelection();
        if (sel && sel.toString().length > 0) {
            applyStyleToSelection('bold');
        } else {
            toggleFontWeight(selectedElement);
        }
        // Keep element selected after styling
        setShowFormattingToolbar(true);
    };

    const handleItalicClick = (e) => {
        e.preventDefault();
        const sel = window.getSelection();
        if (sel && sel.toString().length > 0) {
            applyStyleToSelection('italic');
        } else {
            toggleFontStyle(selectedElement);
        }
        setShowFormattingToolbar(true);
    };

    const handleColorClick = (colorValue) => {
        const sel = window.getSelection();
        if (sel && sel.toString().length > 0) {
            applyStyleToSelection('foreColor', colorValue);
        } else {
            const props = getElementProps(selectedElement);
            if (props) {
                props.setColor(colorValue);
            }
        }
        setShowFormattingToolbar(true);
    };

    const handleBlur = (e, elementId) => {
        const html = e.currentTarget.innerHTML;
        const props = getElementProps(elementId);
        if (props) {
            props.setText(html);
        }
        setIsEditing(false);
    };

    const handleElementClick = (e, elementId) => {
        e.stopPropagation();
        // Only select if not dragging
        if (!isDragging && selectedElement !== elementId) {
            setSelectedElement(elementId);
            setShowFormattingToolbar(true);
            setIsEditing(true);
        }
    };

    const handleDeselect = () => {
        setSelectedElement(null);
        setShowFormattingToolbar(false);
        setIsEditing(false);
        // Clear any text selection
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
    };

    const handleNext = async () => {
        if (!selectedFront || !selectedBack) {
            alert("Please select both a front and back cover.");
            return;
        }

        try {
            setSaving(true);

            let finalFront = selectedFront;
            let finalBack = selectedBack;

            if (selectedFront && !selectedFront.includes("cloudinary.com")) {
                try {
                    const res = await uploadToCloudinary(selectedFront);
                    finalFront = res.url;
                } catch (err) {
                    console.error("Error uploading front cover:", err);
                }
            }

            if (selectedBack && !selectedBack.includes("cloudinary.com")) {
                try {
                    const res = await uploadToCloudinary(selectedBack);
                    finalBack = res.url;
                } catch (err) {
                    console.error("Error uploading back cover:", err);
                }
            }

            await updateBook(bookId, {
                frontCover: finalFront,
                backCover: finalBack,
                coverMode,
                coverBgColor,
                coverFont,
                titleColor,
                titleSize,
                titleTop,
                titleLeft,
                titleWidth,
                titleFontWeight,
                titleFontStyle,
                authorColor,
                authorSize,
                authorTop,
                authorLeft,
                authorWidth,
                authorFontWeight,
                authorFontStyle,
                descColor,
                descSize,
                descTop,
                descLeft,
                descWidth,
                descFontWeight,
                descFontStyle,
                profileColor,
                profileSize,
                profileTop,
                profileLeft,
                profileWidth,
                profileFontWeight,
                profileFontStyle,
                coverTitle,
                coverAuthor,
                coverDescription,
                coverProfileText,
                titleAlign,
                authorAlign,
                descAlign,
                profileAlign,
                titleCase,
                authorCase,
                descCase,
                profileCase,
            });
            navigate(`/upload?bookId=${bookId}`);
        } catch (error) {
            console.error("Error saving covers:", error);
            alert("Failed to save cover selections.");
        } finally {
            setSaving(false);
        }
    };

    // Render formatting toolbar
    const renderFormattingToolbar = () => {
        const elementData = getCurrentElementData();
        if (!elementData) return null;

        const isBold = elementData.fontWeight === "bold";
        const isItalic = elementData.fontStyle === "italic";

        return (
            <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-gray-200 flex-wrap">
                <div className="flex items-center gap-1 flex-wrap">
                    <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleBoldClick}
                        className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold cursor-pointer transition-all duration-200 min-w-7 h-7 flex items-center justify-center ${isBold ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        title="Bold"
                    >
                        <b>B</b>
                    </button>
                    <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleItalicClick}
                        className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold cursor-pointer transition-all duration-200 min-w-7 h-7 flex items-center justify-center ${isItalic ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        title="Italic"
                    >
                        <i>I</i>
                    </button>
                    <div className="w-[1px] h-6 bg-gray-300 mx-1" />

                    <input
                        type="number"
                        value={elementData.size}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val > 0) {
                                elementData.setSize(val);
                            }
                        }}
                        className="w-12 h-7 px-1 border border-gray-300 rounded text-xs text-center outline-none bg-white"
                        min="8"
                        max="72"
                        title="Font Size"
                    />

                    <div className="w-[1px] h-6 bg-gray-300 mx-1" />

                    <input
                        type="color"
                        value={elementData.color}
                        onChange={(e) => handleColorClick(e.target.value)}
                        className="w-7 h-7 p-0 border-none rounded cursor-pointer bg-transparent"
                        title="Font Color"
                    />

                    <div className="w-[1px] h-6 bg-gray-300 mx-1" />

                    <button
                        onClick={() => elementData.setAlign("left")}
                        className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold cursor-pointer transition-all duration-200 min-w-7 h-7 flex items-center justify-center ${elementData.align === "left" ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        title="Align Left"
                    >
                        ⬅
                    </button>
                    <button
                        onClick={() => elementData.setAlign("center")}
                        className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold cursor-pointer transition-all duration-200 min-w-7 h-7 flex items-center justify-center ${elementData.align === "center" ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        title="Align Center"
                    >
                        ⬌
                    </button>
                    <button
                        onClick={() => elementData.setAlign("right")}
                        className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold cursor-pointer transition-all duration-200 min-w-7 h-7 flex items-center justify-center ${elementData.align === "right" ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        title="Align Right"
                    >
                        ➡
                    </button>

                    <div className="w-[1px] h-6 bg-gray-300 mx-1" />

                    <select
                        value={elementData.case}
                        onChange={(e) => elementData.setCase(e.target.value)}
                        className="h-7 px-1 border border-gray-300 rounded text-[11px] bg-white cursor-pointer outline-none"
                    >
                        <option value="normal">Normal</option>
                        <option value="uppercase">UPPERCASE</option>
                        <option value="lowercase">lowercase</option>
                        <option value="capitalize">Title Case</option>
                    </select>

                    <div className="w-[1px] h-6 bg-gray-300 mx-1" />

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            handleDeselect();
                        }}
                        className="px-2.5 h-7 bg-[#10B981] hover:bg-[#10B981]/90 text-white font-bold rounded-md cursor-pointer border-none flex items-center gap-1 text-xs transition-all duration-200 w-auto"
                        title="Done editing (OK)"
                    >
                        OK ✓
                    </button>
                </div>
            </div>
        );
    };

    const renderDraggableElement = (elementId, text, color, size, fontWeight, fontStyle, align, top, left, width, caseType, placeholder) => {
        const isSelected = selectedElement === elementId;
        const props = getElementProps(elementId);

        return (
            <div
                ref={(el) => {
                    if (el) elementRefs.current[elementId] = el;
                }}
                style={{
                    position: "absolute",
                    top: `${top}%`,
                    left: `${left}%`,
                    width: `${width}%`,
                    padding: "4px 8px",
                    border: isSelected ? "2px solid #DC2626" : "2px solid transparent",
                    backgroundColor: isSelected ? "rgba(220, 38, 38, 0.08)" : "transparent",
                    borderRadius: "4px",
                    zIndex: 10,
                    minHeight: "20px",
                    boxSizing: "border-box",
                    cursor: isSelected ? "text" : "default",
                    transition: "border-color 0.2s ease, background-color 0.2s ease",
                }}
                onClick={(e) => {
                    // Only handle click if not dragging
                    if (!isDragging) {
                        handleElementClick(e, elementId);
                    }
                }}
                onMouseDown={(e) => {
                    // Only start drag if clicking on the drag handle
                    if (e.target.closest('.drag-handle')) {
                        startDrag(e, elementId);
                    }
                }}
                onTouchStart={(e) => {
                    if (e.target.closest('.drag-handle')) {
                        startDrag(e, elementId);
                    }
                }}
            >
                {/* Drag handle - ONLY for dragging, appears when selected */}
                {isSelected && (
                    <div
                        className="drag-handle"
                        style={{
                            position: "absolute",
                            top: "-22px",
                            left: "0",
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#DC2626",
                            color: "#ffffff",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "grab",
                            fontSize: "12px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            zIndex: 30,
                            userSelect: "none",
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            startDrag(e, elementId);
                        }}
                        onTouchStart={(e) => {
                            e.stopPropagation();
                            startDrag(e, elementId);
                        }}
                        title="Drag to move (click and drag this handle)"
                    >
                        ✥
                    </div>
                )}

                {/* OK/Done button - appears when selected */}
                {isSelected && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeselect();
                        }}
                        style={{
                            position: "absolute",
                            top: "-22px",
                            right: "0",
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#10B981",
                            color: "#ffffff",
                            borderRadius: "4px",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "10px",
                            fontWeight: "bold",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            zIndex: 30,
                        }}
                        title="Finish editing (OK)"
                    >
                        ✓
                    </button>
                )}

                {/* The contenteditable text block */}
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        color: color,
                        fontSize: `${size}px`,
                        fontFamily: getFontFamily(coverFont),
                        fontWeight: fontWeight,
                        fontStyle: fontStyle,
                        textAlign: align,
                        cursor: isSelected ? "text" : "default",
                        userSelect: isSelected ? "text" : "none",
                        wordBreak: "break-word",
                        textShadow: elementId === "title" || elementId === "author" ? "0 2px 4px rgba(0,0,0,0.8)" : "none",
                        textTransform: caseType || "none",
                        outline: "none",
                        pointerEvents: isSelected ? "auto" : "none",
                        minHeight: "20px",
                    }}
                    contentEditable={isSelected}
                    suppressContentEditableWarning={true}
                    onFocus={() => {
                        setIsEditing(true);
                    }}
                    onBlur={(e) => handleBlur(e, elementId)}
                    onSelect={saveSelection}
                    onMouseUp={saveSelection}
                    onKeyUp={saveSelection}
                    onMouseDown={(e) => {
                        if (isSelected) {
                            e.stopPropagation();
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: text || placeholder }}
                />

                {/* Resize handles - only show when selected */}
                {isSelected && (
                    <>
                        <div
                            className="resize-handle"
                            style={{
                                position: "absolute",
                                bottom: "-6px",
                                right: "-6px",
                                width: "12px",
                                height: "12px",
                                backgroundColor: "#DC2626",
                                border: "2px solid #ffffff",
                                borderRadius: "50%",
                                cursor: "se-resize",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 20,
                            }}
                            onMouseDown={(e) => startResize(e, elementId)}
                            onTouchStart={(e) => startResize(e, elementId)}
                        />
                        <div
                            className="resize-handle"
                            style={{
                                position: "absolute",
                                bottom: "-6px",
                                left: "-6px",
                                width: "12px",
                                height: "12px",
                                backgroundColor: "#DC2626",
                                border: "2px solid #ffffff",
                                borderRadius: "50%",
                                cursor: "sw-resize",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 20,
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const handleLeftResize = (e) => {
                                    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                                    const parent = elementRefs.current[elementId]?.parentElement;
                                    if (!parent) return;
                                    const parentRect = parent.getBoundingClientRect();
                                    const rect = elementRefs.current[elementId].getBoundingClientRect();
                                    const newLeft = ((clientX - parentRect.left) / parentRect.width) * 100;
                                    const newWidth = ((rect.right - clientX) / parentRect.width) * 100;
                                    if (newWidth > 15 && newWidth < 85) {
                                        props.setLeft(Math.max(0, Math.min(85, newLeft)));
                                        props.setWidth(Math.max(15, Math.min(85, newWidth)));
                                    }
                                };
                                const handleUp = () => {
                                    window.removeEventListener("mousemove", handleLeftResize);
                                    window.removeEventListener("mouseup", handleUp);
                                    window.removeEventListener("touchmove", handleLeftResize);
                                    window.removeEventListener("touchend", handleUp);
                                };
                                window.addEventListener("mousemove", handleLeftResize);
                                window.addEventListener("mouseup", handleUp);
                                window.addEventListener("touchmove", handleLeftResize);
                                window.addEventListener("touchend", handleUp);
                            }}
                            onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const handleLeftResize = (e) => {
                                    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                                    const parent = elementRefs.current[elementId]?.parentElement;
                                    if (!parent) return;
                                    const parentRect = parent.getBoundingClientRect();
                                    const rect = elementRefs.current[elementId].getBoundingClientRect();
                                    const newLeft = ((clientX - parentRect.left) / parentRect.width) * 100;
                                    const newWidth = ((rect.right - clientX) / parentRect.width) * 100;
                                    if (newWidth > 15 && newWidth < 85) {
                                        props.setLeft(Math.max(0, Math.min(85, newLeft)));
                                        props.setWidth(Math.max(15, Math.min(85, newWidth)));
                                    }
                                };
                                const handleUp = () => {
                                    window.removeEventListener("touchmove", handleLeftResize);
                                    window.removeEventListener("touchend", handleUp);
                                };
                                window.addEventListener("touchmove", handleLeftResize);
                                window.addEventListener("touchend", handleUp);
                            }}
                        />
                        <div
                            className="resize-handle"
                            style={{
                                position: "absolute",
                                top: "-6px",
                                right: "-6px",
                                width: "12px",
                                height: "12px",
                                backgroundColor: "#DC2626",
                                border: "2px solid #ffffff",
                                borderRadius: "50%",
                                cursor: "ne-resize",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 20,
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const handleTopResize = (e) => {
                                    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                                    const parent = elementRefs.current[elementId]?.parentElement;
                                    if (!parent) return;
                                    const parentRect = parent.getBoundingClientRect();
                                    const rect = elementRefs.current[elementId].getBoundingClientRect();
                                    const newTop = ((clientY - parentRect.top) / parentRect.height) * 100;
                                    if (newTop > 0 && newTop < 90) {
                                        props.setTop(Math.max(0, Math.min(90, newTop)));
                                    }
                                };
                                const handleUp = () => {
                                    window.removeEventListener("mousemove", handleTopResize);
                                    window.removeEventListener("mouseup", handleUp);
                                    window.removeEventListener("touchmove", handleTopResize);
                                    window.removeEventListener("touchend", handleUp);
                                };
                                window.addEventListener("mousemove", handleTopResize);
                                window.addEventListener("mouseup", handleUp);
                                window.addEventListener("touchmove", handleTopResize);
                                window.addEventListener("touchend", handleUp);
                            }}
                            onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const handleTopResize = (e) => {
                                    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                                    const parent = elementRefs.current[elementId]?.parentElement;
                                    if (!parent) return;
                                    const parentRect = parent.getBoundingClientRect();
                                    const rect = elementRefs.current[elementId].getBoundingClientRect();
                                    const newTop = ((clientY - parentRect.top) / parentRect.height) * 100;
                                    if (newTop > 0 && newTop < 90) {
                                        props.setTop(Math.max(0, Math.min(90, newTop)));
                                    }
                                };
                                const handleUp = () => {
                                    window.removeEventListener("touchmove", handleTopResize);
                                    window.removeEventListener("touchend", handleUp);
                                };
                                window.addEventListener("touchmove", handleTopResize);
                                window.addEventListener("touchend", handleUp);
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                bottom: "-24px",
                                right: "0",
                                fontSize: "10px",
                                color: "#DC2626",
                                backgroundColor: "rgba(255,255,255,0.9)",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #DC2626",
                                fontWeight: "bold",
                                pointerEvents: "none",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {Math.round(width)}%
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderBackCoverElements = () => {
        return (
            <>
                {renderDraggableElement(
                    "description",
                    coverDescription || book?.aboutAuthor || `${book?.author || "The author"} is the visionary creator of this book.`,
                    descColor,
                    descSize,
                    descFontWeight,
                    descFontStyle,
                    descAlign,
                    descTop,
                    descLeft,
                    descWidth,
                    descCase,
                    "Enter description"
                )}

                <div
                    ref={(el) => {
                        if (el) elementRefs.current["profile"] = el;
                    }}
                    style={{
                        position: "absolute",
                        top: `${profileTop}%`,
                        left: `${profileLeft}%`,
                        width: `${profileWidth}%`,
                        padding: "8px",
                        border: selectedElement === "profile" ? "2px solid #DC2626" : "2px solid transparent",
                        backgroundColor: selectedElement === "profile" ? "rgba(220, 38, 38, 0.15)" : "rgba(0, 0, 0, 0.6)",
                        borderRadius: "8px",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        backdropFilter: "blur(2px)",
                        boxSizing: "border-box",
                        cursor: selectedElement === "profile" ? "default" : "default",
                        transition: "border-color 0.2s ease, background-color 0.2s ease",
                    }}
                    onClick={(e) => {
                        if (!isDragging) {
                            handleElementClick(e, "profile");
                        }
                    }}
                    onMouseDown={(e) => {
                        if (e.target.closest('.drag-handle')) {
                            startDrag(e, "profile");
                        }
                    }}
                    onTouchStart={(e) => {
                        if (e.target.closest('.drag-handle')) {
                            startDrag(e, "profile");
                        }
                    }}
                >
                    {selectedElement === "profile" && (
                        <div
                            className="drag-handle"
                            style={{
                                position: "absolute",
                                top: "-22px",
                                left: "0",
                                width: "20px",
                                height: "20px",
                                backgroundColor: "#DC2626",
                                color: "#ffffff",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "grab",
                                fontSize: "12px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 30,
                                userSelect: "none",
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                startDrag(e, "profile");
                            }}
                            onTouchStart={(e) => {
                                e.stopPropagation();
                                startDrag(e, "profile");
                            }}
                            title="Drag to move"
                        >
                            ✥
                        </div>
                    )}

                    {selectedElement === "profile" && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeselect();
                            }}
                            style={{
                                position: "absolute",
                                top: "-22px",
                                right: "0",
                                width: "20px",
                                height: "20px",
                                backgroundColor: "#10B981",
                                color: "#ffffff",
                                borderRadius: "4px",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: "10px",
                                fontWeight: "bold",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 30,
                            }}
                            title="Finish editing (OK)"
                        >
                            ✓
                        </button>
                    )}

                    <img
                        src={book?.authorPhoto || `https://image.pollinations.ai/prompt/professional%20headshot%20portrait%20of%20author%20named%20${encodeURIComponent(book?.author || "author")}%20wearing%20smart%20casual%20attire%20studio%20lighting%20plain%20background?width=100&height=100&nologo=true&seed=42`}
                        alt="Author"
                        className="w-10 h-10 rounded-full object-cover border border-white/40 pointer-events-none select-none"
                        onError={(e) => {
                            e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=60";
                        }}
                    />
                    <div style={{
                        flex: 1,
                        textAlign: profileAlign,
                        pointerEvents: selectedElement === "profile" ? "auto" : "none",
                        width: "100%"
                    }}>
                        <div
                            id="profile-author-text"
                            contentEditable={selectedElement === "profile"}
                            suppressContentEditableWarning={true}
                            onFocus={() => setIsEditing(true)}
                            onBlur={(e) => setCoverAuthor(e.currentTarget.innerHTML)}
                            onSelect={saveSelection}
                            onMouseUp={saveSelection}
                            onKeyUp={saveSelection}
                            style={{
                                fontSize: `${profileSize + 2}px`,
                                fontWeight: authorFontWeight,
                                fontStyle: authorFontStyle,
                                lineHeight: 1.1,
                                outline: "none",
                                cursor: selectedElement === "profile" ? "text" : "default",
                                userSelect: selectedElement === "profile" ? "text" : "none",
                                textTransform: authorCase || "none",
                            }}
                            dangerouslySetInnerHTML={{ __html: coverAuthor || book?.author || "Author" }}
                        />
                        <div
                            id="profile-bio-text"
                            contentEditable={selectedElement === "profile"}
                            suppressContentEditableWarning={true}
                            onFocus={() => setIsEditing(true)}
                            onBlur={(e) => setCoverProfileText(e.currentTarget.innerHTML)}
                            onSelect={saveSelection}
                            onMouseUp={saveSelection}
                            onKeyUp={saveSelection}
                            style={{
                                fontSize: `${profileSize}px`,
                                fontWeight: profileFontWeight,
                                fontStyle: profileFontStyle,
                                opacity: 0.9,
                                marginTop: "2px",
                                lineHeight: 1.2,
                                outline: "none",
                                cursor: selectedElement === "profile" ? "text" : "default",
                                userSelect: selectedElement === "profile" ? "text" : "none",
                                textTransform: profileCase || "none",
                            }}
                            dangerouslySetInnerHTML={{ __html: coverProfileText || book?.description || "A fascinating journey through character and destiny." }}
                        />
                    </div>

                    {selectedElement === "profile" && (
                        <>
                            <div
                                className="resize-handle"
                                style={{
                                    position: "absolute",
                                    bottom: "-6px",
                                    right: "-6px",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#DC2626",
                                    border: "2px solid #ffffff",
                                    borderRadius: "50%",
                                    cursor: "se-resize",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                    zIndex: 20,
                                }}
                                onMouseDown={(e) => startResize(e, "profile")}
                                onTouchStart={(e) => startResize(e, "profile")}
                            />
                            <div
                                className="resize-handle"
                                style={{
                                    position: "absolute",
                                    bottom: "-6px",
                                    left: "-6px",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#DC2626",
                                    border: "2px solid #ffffff",
                                    borderRadius: "50%",
                                    cursor: "sw-resize",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                    zIndex: 20,
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const props = getElementProps("profile");
                                    const handleLeftResize = (e) => {
                                        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                                        const parent = elementRefs.current["profile"]?.parentElement;
                                        if (!parent) return;
                                        const parentRect = parent.getBoundingClientRect();
                                        const rect = elementRefs.current["profile"].getBoundingClientRect();
                                        const newLeft = ((clientX - parentRect.left) / parentRect.width) * 100;
                                        const newWidth = ((rect.right - clientX) / parentRect.width) * 100;
                                        if (newWidth > 15 && newWidth < 85) {
                                            props.setLeft(Math.max(0, Math.min(85, newLeft)));
                                            props.setWidth(Math.max(15, Math.min(85, newWidth)));
                                        }
                                    };
                                    const handleUp = () => {
                                        window.removeEventListener("mousemove", handleLeftResize);
                                        window.removeEventListener("mouseup", handleUp);
                                        window.removeEventListener("touchmove", handleLeftResize);
                                        window.removeEventListener("touchend", handleUp);
                                    };
                                    window.addEventListener("mousemove", handleLeftResize);
                                    window.addEventListener("mouseup", handleUp);
                                    window.addEventListener("touchmove", handleLeftResize);
                                    window.addEventListener("touchend", handleUp);
                                }}
                                onTouchStart={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const props = getElementProps("profile");
                                    const handleLeftResize = (e) => {
                                        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                                        const parent = elementRefs.current["profile"]?.parentElement;
                                        if (!parent) return;
                                        const parentRect = parent.getBoundingClientRect();
                                        const rect = elementRefs.current["profile"].getBoundingClientRect();
                                        const newLeft = ((clientX - parentRect.left) / parentRect.width) * 100;
                                        const newWidth = ((rect.right - clientX) / parentRect.width) * 100;
                                        if (newWidth > 15 && newWidth < 85) {
                                            props.setLeft(Math.max(0, Math.min(85, newLeft)));
                                            props.setWidth(Math.max(15, Math.min(85, newWidth)));
                                        }
                                    };
                                    const handleUp = () => {
                                        window.removeEventListener("touchmove", handleLeftResize);
                                        window.removeEventListener("touchend", handleUp);
                                    };
                                    window.addEventListener("touchmove", handleLeftResize);
                                    window.addEventListener("touchend", handleUp);
                                }}
                            />
                            <div
                                className="resize-handle"
                                style={{
                                    position: "absolute",
                                    top: "-6px",
                                    right: "-6px",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#DC2626",
                                    border: "2px solid #ffffff",
                                    borderRadius: "50%",
                                    cursor: "ne-resize",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                    zIndex: 20,
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const props = getElementProps("profile");
                                    const handleTopResize = (e) => {
                                        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                                        const parent = elementRefs.current["profile"]?.parentElement;
                                        if (!parent) return;
                                        const parentRect = parent.getBoundingClientRect();
                                        const rect = elementRefs.current["profile"].getBoundingClientRect();
                                        const newTop = ((clientY - parentRect.top) / parentRect.height) * 100;
                                        if (newTop > 0 && newTop < 90) {
                                            props.setTop(Math.max(0, Math.min(90, newTop)));
                                        }
                                    };
                                    const handleUp = () => {
                                        window.removeEventListener("mousemove", handleTopResize);
                                        window.removeEventListener("mouseup", handleUp);
                                        window.removeEventListener("touchmove", handleTopResize);
                                        window.removeEventListener("touchend", handleUp);
                                    };
                                    window.addEventListener("mousemove", handleTopResize);
                                    window.addEventListener("mouseup", handleUp);
                                    window.addEventListener("touchmove", handleTopResize);
                                    window.addEventListener("touchend", handleUp);
                                }}
                                onTouchStart={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const props = getElementProps("profile");
                                    const handleTopResize = (e) => {
                                        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                                        const parent = elementRefs.current["profile"]?.parentElement;
                                        if (!parent) return;
                                        const parentRect = parent.getBoundingClientRect();
                                        const rect = elementRefs.current["profile"].getBoundingClientRect();
                                        const newTop = ((clientY - parentRect.top) / parentRect.height) * 100;
                                        if (newTop > 0 && newTop < 90) {
                                            props.setTop(Math.max(0, Math.min(90, newTop)));
                                        }
                                    };
                                    const handleUp = () => {
                                        window.removeEventListener("touchmove", handleTopResize);
                                        window.removeEventListener("touchend", handleUp);
                                    };
                                    window.addEventListener("touchmove", handleTopResize);
                                    window.addEventListener("touchend", handleUp);
                                }}
                            />
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: "-24px",
                                    right: "0",
                                    fontSize: "10px",
                                    color: "#DC2626",
                                    backgroundColor: "rgba(255,255,255,0.9)",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    border: "1px solid #DC2626",
                                    fontWeight: "bold",
                                    pointerEvents: "none",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {Math.round(profileWidth)}%
                            </div>
                        </>
                    )}
                </div>

                <div className="absolute bottom-4 right-4 bg-white p-0.5 rounded flex flex-col items-center justify-center h-7 w-12 shadow-md border border-gray-200 z-10 pointer-events-none">
                    <div className="flex items-end justify-center space-x-[0.5px] h-4 w-full">
                        {Array.from({ length: 11 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-black"
                                style={{
                                    width: i % 3 === 0 ? '1.5px' : '0.8px',
                                    height: i % 4 === 0 ? '100%' : '80%',
                                }}
                            />
                        ))}
                    </div>
                    <span className="text-[3px] text-gray-800 font-mono tracking-tighter leading-none scale-[0.8]">
                        978-3-16-148410-0
                    </span>
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F8F7F4] to-[#F0EDE6] font-serif">
            <nav className="bg-white px-6 md:px-10 flex items-center justify-between h-[72px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Pub<span className="text-[#DC2626]">Tech</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    <Link to="/dashboard" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">
                        Dashboard
                    </Link>
                    <Link to="/create-book" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">
                        Create Book
                    </Link>
                    <Link to="/covers" className="text-[#1E3A8A] border-b-2 border-[#DC2626] text-sm font-medium transition-colors duration-150 no-underline py-2">
                        Covers
                    </Link>
                    <Link to="/upload" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">
                        Upload
                    </Link>
                    <Link to="/preview" className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2">
                        Preview
                    </Link>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs font-semibold hidden sm:inline">{user?.email}</span>
                    <button onClick={handleLogout} className="px-4 py-2 bg-transparent hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150">
                        Log Out
                    </button>
                </div>
            </nav>

            <div className="py-10 px-4">
                {loading ? (
                    <div className="max-w-7xl mx-auto">
                        <Stepper currentStep={2} />
                        <div className="mt-8 flex flex-col items-center justify-center p-16 bg-white rounded-2xl shadow-xl border border-gray-100">
                            <div className="relative">
                                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-red-600"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-2xl">🎨</span>
                                </div>
                            </div>
                            <p className="mt-6 text-gray-700 font-medium text-lg animate-pulse">
                                Generating Cover Designs with AI...
                            </p>
                            <p className="text-sm text-gray-500 mt-2">Creating unique covers for your book</p>
                        </div>
                    </div>
                ) : !bookId ? (
                    <div className="max-w-3xl mx-auto text-center bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">📖</span>
                        </div>
                        <h2 className="text-2xl font-bold text-blue-900 mb-3">No active book selection</h2>
                        <p className="text-gray-600 mb-8">Please create a book first before selecting covers.</p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                ) : (
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <div className="mb-8 text-center">
                            <div className="inline-flex items-center gap-2 bg-blue-900/10 border border-blue-900/20 rounded-full px-4 py-1.5 mb-4">
                                <span className="w-1.5 h-1.5 bg-red-600 rounded-full inline-block" />
                                <span className="text-blue-900 text-xs font-bold tracking-widest uppercase">
                                    Step 2 of 3
                                </span>
                            </div>
                            <h1 className="text-4xl font-extrabold text-blue-900 mb-3 tracking-tight">
                                Choose Your <span className="text-red-600">Covers</span>
                            </h1>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                Select the perfect front and back covers for your book from our AI-generated options.
                            </p>
                        </div>

                        <Stepper currentStep={2} />

                        {/* Book Info Card */}
                        <div className="mt-8 bg-gradient-to-r from-blue-50 to-white rounded-2xl shadow-lg border border-blue-100 p-6 mb-8">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-xl font-bold text-blue-900">
                                        {book?.title}
                                    </h2>
                                    <p className="text-gray-600 text-sm">
                                        by {book?.author} • {book?.genre}
                                    </p>
                                </div>
                                <div className="bg-blue-900/10 px-4 py-2 rounded-lg border border-blue-900/20">
                                    <span className="text-xs font-bold text-blue-900 uppercase tracking-wider">
                                        Concept: <span className="text-red-600">{book?.selectedSuggestion?.theme || "Custom"}</span>
                                    </span>
                                </div>
                            </div>

                            {book?.selectedSuggestion && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/60 p-4 rounded-xl border border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Colors:</span>
                                        <span className="text-sm font-semibold text-gray-800">{book.selectedSuggestion.colors}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Art Concept:</span>
                                        <span className="text-sm text-gray-700 italic">"{book.selectedSuggestion.imageIdea}"</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-10">
                            {/* Phase 1: Galleries Selection */}
                            <div className="space-y-8 mb-10">
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-red-600 to-blue-900" />
                                    <div className="p-6">
                                        <CoverGallery
                                            title="1. Select Front Cover template"
                                            covers={frontCovers}
                                            selectedCover={selectedFront}
                                            onSelect={setSelectedFront}
                                            bookTitle={book?.title}
                                            bookAuthor={book?.author}
                                            colors={book?.selectedSuggestion?.colors}
                                            isBackCover={false}
                                            coverMode={coverMode}
                                            coverBgColor={coverBgColor}
                                            coverTextColor={titleColor}
                                            coverPosition="center"
                                            coverFont={coverFont}
                                            bookType={book?.bookType}
                                        />
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-blue-900 to-red-600" />
                                    <div className="p-6">
                                        <CoverGallery
                                            title="2. Select Back Cover template"
                                            covers={backCovers}
                                            selectedCover={selectedBack}
                                            onSelect={setSelectedBack}
                                            bookTitle={book?.title}
                                            bookAuthor={book?.author}
                                            colors={book?.selectedSuggestion?.colors}
                                            bookDescription={book?.aboutAuthor}
                                            isBackCover={true}
                                            coverMode={coverMode}
                                            coverBgColor={coverBgColor}
                                            coverTextColor={descColor}
                                            coverPosition="center"
                                            coverFont={coverFont}
                                            bookType={book?.bookType}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Phase 2: Interactive Cover Designer */}
                            {selectedFront && selectedBack && (
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-red-600 via-blue-900 to-red-600" />
                                    <div className="p-8">
                                        <h2 className="text-2xl font-bold text-blue-900 mb-2 flex items-center gap-2">
                                            <span>🎨</span> Personalize Selected Cover
                                        </h2>
                                        <p className="text-gray-600 mb-6 text-sm">
                                            <strong>Click</strong> on any text block to select it for editing, <strong>drag the ✥ handle</strong> to reposition, and <strong>drag the red resize handles</strong> to resize. Use the toolbar below for styling. Click <strong>OK ✓</strong> or click outside to finish editing.
                                        </p>

                                        {/* Tabs */}
                                        <div className="flex gap-4 border-b border-gray-200 pb-4 mb-6">
                                            <button
                                                onClick={() => {
                                                    setActiveDesignTab("front");
                                                    handleDeselect();
                                                }}
                                                className={`pb-2 px-4 font-bold text-sm border-b-2 transition-all ${activeDesignTab === "front" ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                                            >
                                                📖 Front Cover
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setActiveDesignTab("back");
                                                    handleDeselect();
                                                }}
                                                className={`pb-2 px-4 font-bold text-sm border-b-2 transition-all ${activeDesignTab === "back" ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                                            >
                                                📖 Back Cover
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                                            {/* Left Side: Interactive Canvas */}
                                            <div className="lg:col-span-2 flex justify-center items-center bg-gray-50 p-6 rounded-xl border border-dashed border-gray-200">
                                                <div
                                                    onClick={() => {
                                                        if (!isDragging) {
                                                            handleDeselect();
                                                        }
                                                    }}
                                                    style={{
                                                        width: "100%",
                                                        maxWidth: "320px",
                                                        aspectRatio: getBookAspect(book?.bookType),
                                                        position: "relative",
                                                        overflow: "hidden",
                                                        borderRadius: "12px",
                                                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)",
                                                        userSelect: "none",
                                                        ...(coverMode === "solid"
                                                            ? { backgroundColor: coverBgColor }
                                                            : {
                                                                backgroundImage: `url(${activeDesignTab === "front" ? selectedFront : selectedBack})`,
                                                                backgroundSize: "cover",
                                                                backgroundPosition: "center",
                                                            })
                                                    }}
                                                    className="border-2 border-gray-300 relative"
                                                >
                                                    {/* Gradient overlay for better text readability */}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20 pointer-events-none z-0" />

                                                    {activeDesignTab === "front" ? (
                                                        <>
                                                            {renderDraggableElement(
                                                                "title",
                                                                coverTitle || book?.title || "Untitled",
                                                                titleColor,
                                                                titleSize,
                                                                titleFontWeight,
                                                                titleFontStyle,
                                                                titleAlign,
                                                                titleTop,
                                                                titleLeft,
                                                                titleWidth,
                                                                titleCase,
                                                                "Enter title"
                                                            )}

                                                            {renderDraggableElement(
                                                                "author",
                                                                coverAuthor || book?.author || "Author",
                                                                authorColor,
                                                                authorSize,
                                                                authorFontWeight,
                                                                authorFontStyle,
                                                                authorAlign,
                                                                authorTop,
                                                                authorLeft,
                                                                authorWidth,
                                                                authorCase,
                                                                "Enter author"
                                                            )}
                                                        </>
                                                    ) : (
                                                        renderBackCoverElements()
                                                    )}
                                                </div>
                                            </div>

                                            {/* Right Side: Control Panel */}
                                            <div className="lg:col-span-3 bg-gray-50/50 p-6 rounded-xl border border-gray-150 flex flex-col justify-between overflow-hidden">
                                                <div>
                                                    <h3 className="text-md font-bold text-blue-900 border-b pb-3 mb-4 flex items-center gap-2">
                                                        <span>⚙️</span> Styling Controls
                                                    </h3>

                                                    {selectedElement ? (
                                                        <div className="flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden mb-4 shadow-sm">
                                                            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                                                                <span className="text-xs font-bold text-gray-700">Editing Cover Text: <span className="text-red-600 uppercase">{selectedElement}</span></span>
                                                                <button
                                                                    onClick={handleDeselect}
                                                                    className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold shadow-xs transition-colors"
                                                                >
                                                                    ✓ Done
                                                                </button>
                                                            </div>
                                                            <div className="h-[400px] relative">
                                                                <DocxEditorModal
                                                                    isOpen={true}
                                                                    isModal={false}
                                                                    initialHtml={getElementProps(selectedElement)?.text || ""}
                                                                    onChange={(html) => {
                                                                        const props = getElementProps(selectedElement);
                                                                        if (props) props.setText(html);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        /* General Layout Controls */
                                                        <div className="space-y-4">
                                                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                                                                <p className="text-xs text-blue-800 font-medium">
                                                                    💡 <strong>Tip:</strong> Click on any text block on the cover (like the Title or Author) to edit its text and style using the inline MS Word Editor.<br />
                                                                    ✥ <strong>Move:</strong> Drag the ✥ handle on the top-left of the selected text box.<br />
                                                                    🔴 <strong>Resize:</strong> Drag the red circles on the corners of the selected text box.
                                                                </p>
                                                            </div>

                                                            {/* Cover Mode Selection */}
                                                            <div>
                                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Cover Layout Mode</label>
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <button
                                                                        onClick={() => setCoverMode("image")}
                                                                        className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all ${coverMode === "image" ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"}`}
                                                                    >
                                                                        AI Generated Art
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setCoverMode("solid")}
                                                                        className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all ${coverMode === "solid" ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"}`}
                                                                    >
                                                                        Minimalist Solid
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Background Color (Solid mode only) */}
                                                            {coverMode === "solid" && (
                                                                <div>
                                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Cover Background Color</label>
                                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                                        {["#DC2626", "#1E3A8A", "#111827", "#15803D", "#D97706", "#78350F"].map(color => (
                                                                            <button
                                                                                key={color}
                                                                                onClick={() => setCoverBgColor(color)}
                                                                                style={{ backgroundColor: color }}
                                                                                className={`w-6 h-6 rounded-full border-2 transition-transform ${coverBgColor === color ? "border-black scale-110 shadow-xs" : "border-transparent"}`}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <input
                                                                            type="color"
                                                                            value={coverBgColor}
                                                                            onChange={e => setCoverBgColor(e.target.value)}
                                                                            className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                                                                        />
                                                                        <span className="text-xs font-mono text-gray-500 uppercase">{coverBgColor}</span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Typography Font */}
                                                            <div>
                                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Typography Font (Global)</label>
                                                                <select
                                                                    value={coverFont}
                                                                    onChange={e => setCoverFont(e.target.value)}
                                                                    className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white text-gray-700 focus:outline-none focus:border-red-500"
                                                                >
                                                                    <option value="serif">Serif (Georgia / Times)</option>
                                                                    <option value="sans-serif">Sans-serif (Arial / Helvetica)</option>
                                                                    <option value="monospace">Monospace (Courier)</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {selectedElement && (
                                                        <div className="bg-white p-3 rounded-lg border border-gray-200 mt-4">
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="font-bold text-gray-500">Selected Element:</span>
                                                                <span className="font-bold text-red-600 uppercase">{selectedElement}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs mt-1">
                                                                <span className="text-gray-500">Width:</span>
                                                                <span className="font-mono font-bold">{Math.round(getCurrentElementData()?.width || 0)}%</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs mt-1">
                                                                <span className="text-gray-500">Font Size:</span>
                                                                <span className="font-mono font-bold">{getCurrentElementData()?.size || 0}px</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Reset button */}
                                                <button
                                                    onClick={() => {
                                                        setTitleTop(35);
                                                        setTitleLeft(10);
                                                        setTitleWidth(60);
                                                        setTitleSize(28);
                                                        setTitleColor("#FFFFFF");
                                                        setTitleFontWeight("bold");
                                                        setTitleFontStyle("normal");
                                                        setTitleAlign("center");
                                                        setTitleCase("uppercase");

                                                        setAuthorTop(75);
                                                        setAuthorLeft(10);
                                                        setAuthorWidth(60);
                                                        setAuthorSize(16);
                                                        setAuthorColor("#FFFFFF");
                                                        setAuthorFontWeight("bold");
                                                        setAuthorFontStyle("normal");
                                                        setAuthorAlign("center");
                                                        setAuthorCase("uppercase");

                                                        setDescTop(15);
                                                        setDescLeft(8);
                                                        setDescWidth(60);
                                                        setDescSize(13);
                                                        setDescColor("#FFFFFF");
                                                        setDescFontWeight("normal");
                                                        setDescFontStyle("italic");
                                                        setDescAlign("left");
                                                        setDescCase("normal");

                                                        setProfileTop(65);
                                                        setProfileLeft(8);
                                                        setProfileWidth(60);
                                                        setProfileSize(10);
                                                        setProfileColor("#FFFFFF");
                                                        setProfileFontWeight("normal");
                                                        setProfileFontStyle("italic");
                                                        setProfileAlign("left");
                                                        setProfileCase("normal");

                                                        handleDeselect();

                                                        setCoverTitle(book?.title || "");
                                                        setCoverAuthor(book?.author || "");
                                                        setCoverDescription(book?.aboutAuthor || "");
                                                        setCoverProfileText(book?.description || "");
                                                    }}
                                                    className="mt-6 text-xs text-red-600 hover:text-red-700 font-bold border border-red-200 hover:border-red-300 bg-white py-2 px-3 rounded-lg shadow-xs transition-all w-full text-center"
                                                >
                                                    Reset All Styles, Positions & Sizes
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="mt-10 flex justify-between items-center">
                            <button
                                onClick={() => navigate(`/create-book?bookId=${bookId}`)}
                                className="text-gray-600 hover:text-blue-900 font-semibold transition-colors duration-200 flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Concepts
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={saving}
                                className={`bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center gap-2 ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving Selection...
                                    </>
                                ) : (
                                    <>
                                        Continue to Upload
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default CoverSelection;